import { useCallback, useRef, useSyncExternalStore } from "react"

function subscribe(callback: () => void) {
  window.addEventListener("storage", callback)
  return () => window.removeEventListener("storage", callback)
}

export function useLocalStorage<T>(key: string, initialValue?: T) {
  const prevSnapshot = useRef<T>(initialValue)

  const getSnapshot = useCallback(() => {
    const storedValue = localStorage.getItem(key)
    const parsedValue = storedValue ? JSON.parse(storedValue) : initialValue
    if (JSON.stringify(prevSnapshot.current) !== storedValue) {
      prevSnapshot.current = parsedValue
    }
    if (!storedValue && initialValue !== undefined) {
      localStorage.setItem(key, JSON.stringify(initialValue))
    }
    return prevSnapshot.current!
  }, [key, initialValue])

  const store = useSyncExternalStore<T>(subscribe, getSnapshot, getSnapshot)

  const setValue = useCallback(
    (value: T | ((prev: T | null) => T)) => {
      const newValue = value instanceof Function ? value(store) : value
      localStorage.setItem(key, JSON.stringify(newValue))
      window.dispatchEvent(new Event("storage"))
    },
    [key, store]
  )

  return [store, setValue] as const
}

export default useLocalStorage
